/**
 * BANDOPRINZ Model Limit Manager
 * Enforces strict model limits to prevent Safari iOS RAM crashes
 *
 * Limits:
 * - Home page: 1 model max
 * - Collection page: 2 models max
 *
 * Uses LRU (Least Recently Used) eviction strategy
 */

class ModelLimitManager {
  constructor() {
    // Map of viewerId -> { src, timestamp, viewer }
    this.activeModels = new Map();

    // Device-specific state
    this.deviceType = null; // 'desktop' | 'mobile' | 'tablet'
    this.viewportObserver = null; // IntersectionObserver for mobile
    this.visibilityRatios = new Map(); // viewerId -> visibility ratio (0-1)
    this.interactionListeners = new WeakMap(); // viewer -> { pointerdown, touchstart }
    this.pausedModels = new Map(); // viewerId -> { src, viewer, wasAutoRotating }

    // Debounce timer for visibility changes
    this.visibilityDebounceTimer = null;

    // Track if models were disposed due to tab hide (for reload)
    this.disposedOnHide = false;
    this.modelsBeforeHide = []; // Store src/viewer pairs for reload

    // Detect page type and set limit
    this.maxModels = this.detectPageLimit();

    // Debug mode
    this.debug = true;

    this.log('Initialized with max models:', this.maxModels, 'device:', this.deviceType);

    // Initialize device-specific observers after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initDeviceObservers());
    } else {
      // Use setTimeout to ensure model-viewer elements are rendered
      setTimeout(() => this.initDeviceObservers(), 500);
    }

    // Listen for WebGL context lost events (iOS Safari crash recovery)
    this.setupContextLostHandler();
  }

  /**
   * Setup global handler for WebGL context lost events (iOS Safari)
   */
  setupContextLostHandler() {
    // Use capture to catch events from shadow DOM
    document.addEventListener('webglcontextlost', (event) => {
      this.log('⚠️ WebGL context lost detected!');
      event.preventDefault(); // Required to allow restore

      // Find which viewer lost context and mark for reload
      const canvas = event.target;
      if (canvas && canvas.getRootNode) {
        const root = canvas.getRootNode();
        if (root.host && root.host.tagName === 'MODEL-VIEWER') {
          const viewer = root.host;
          const viewerId = this.getViewerId(viewer);
          this.log('Context lost on viewer:', viewerId);

          // Remove from active tracking
          this.activeModels.delete(viewerId);
          this.pausedModels.delete(viewerId);
        }
      }
    }, true);

    document.addEventListener('webglcontextrestored', (event) => {
      this.log('✅ WebGL context restored');
    }, true);
  }

  /**
   * Detect device type based on screen size, touch capability, and user agent
   * @returns {'desktop' | 'mobile' | 'tablet'}
   */
  detectDeviceType() {
    const screenWidth = window.innerWidth || document.documentElement.clientWidth;

    // Check for touch capability (primary input method)
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const hasFinePointer = window.matchMedia('(pointer: fine)').matches;

    // User agent checks for specific devices
    const userAgent = navigator.userAgent.toLowerCase();
    const isIPhone = /iphone/i.test(userAgent);
    // iPad Pro detection: check for iPad in UA, or Mac with touch (iPadOS 13+ reports as Mac)
    const isIPad = /ipad/i.test(userAgent) ||
      (/macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
    const isAndroidPhone = /android/i.test(userAgent) && /mobile/i.test(userAgent);
    const isAndroidTablet = /android/i.test(userAgent) && !/mobile/i.test(userAgent);

    // DESKTOP: Large screen (>1024px) AND fine pointer (mouse) as primary
    if (screenWidth > 1024 && hasFinePointer && !hasCoarsePointer) {
      this.log('Device detected: DESKTOP (width:', screenWidth, ', fine pointer)');
      return 'desktop';
    }

    // MOBILE: iPhone, Android phone, or small viewport with touch
    if (isIPhone || isAndroidPhone || (screenWidth <= 768 && hasCoarsePointer)) {
      this.log('Device detected: MOBILE (iPhone/Android phone or small touch device)');
      return 'mobile';
    }

    // TABLET: iPad, Android tablet, or medium viewport with touch
    if (isIPad || isAndroidTablet || (screenWidth > 768 && screenWidth <= 1024 && hasCoarsePointer)) {
      this.log('Device detected: TABLET (iPad/Android tablet or medium touch device)');
      return 'tablet';
    }

    // FALLBACK: Default to mobile (safest for memory)
    this.log('Device detected: MOBILE (fallback - unknown configuration)');
    return 'mobile';
  }

  detectPageLimit() {
    // Detect device type first
    this.deviceType = this.detectDeviceType();

    // Device-specific limits
    switch (this.deviceType) {
      case 'desktop':
        // Desktop: No optimization - allow many models simultaneously
        this.log('Setting limit: UNLIMITED (desktop mode)');
        return 999; // Effectively unlimited

      case 'tablet':
        // Tablet: Interaction-based - only 1 active, but quick swap
        this.log('Setting limit: 1 (tablet interaction mode)');
        return 1;

      case 'mobile':
      default:
        // Mobile: Viewport-based - only most visible model active
        this.log('Setting limit: 1 (mobile viewport mode)');
        return 1;
    }
  }

  log(...args) {
    if (this.debug) {
      console.log('[ModelLimitManager]', ...args);
    }
  }

  /**
   * Request to load a model - will dispose oldest if at limit
   * @param {Element} viewer - The model-viewer element
   * @param {string} src - The model source URL
   * @returns {boolean} - Whether load can proceed
   */
  async requestLoad(viewer, src) {
    const viewerId = this.getViewerId(viewer);

    // If this viewer already has an active model, dispose it first
    if (this.activeModels.has(viewerId)) {
      this.log('Disposing existing model on viewer:', viewerId);
      await this.disposeModel(viewer);
    }

    // Check if at limit
    if (this.activeModels.size >= this.maxModels) {
      this.log('At limit, disposing oldest model. Current:', this.activeModels.size);
      await this.disposeOldest();
    }

    // Register this model as loading
    this.activeModels.set(viewerId, {
      src: src,
      timestamp: Date.now(),
      viewer: viewer
    });

    this.log('Registered model:', src.split('/').pop(), 'Active count:', this.activeModels.size);

    return true;
  }

  /**
   * Get unique ID for a viewer element
   */
  getViewerId(viewer) {
    if (!viewer.dataset.limitId) {
      viewer.dataset.limitId = 'mv-' + Math.random().toString(36).substr(2, 9);
    }
    return viewer.dataset.limitId;
  }

  /**
   * Full disposal of a model with WebGL context cleanup
   * @param {Element} viewer - The model-viewer element
   */
  async disposeModel(viewer) {
    const viewerId = this.getViewerId(viewer);
    const modelInfo = this.activeModels.get(viewerId);

    if (modelInfo) {
      this.log('Disposing model:', modelInfo.src?.split('/').pop() || 'unknown');
    }

    // 1. Stop any animations/auto-rotate FIRST
    if (viewer.pause) {
      viewer.pause();
    }
    viewer.autoRotate = false;
    viewer.cameraControls = false;

    // 2. Force WebGL context cleanup BEFORE clearing src (more effective)
    // Must be done while model-viewer still has active context
    try {
      const shadowRoot = viewer.shadowRoot;
      if (shadowRoot) {
        const canvas = shadowRoot.querySelector('canvas');
        if (canvas) {
          const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
          if (gl && !gl.isContextLost() && gl.getExtension) {
            const ext = gl.getExtension('WEBGL_lose_context');
            if (ext) {
              ext.loseContext();
              this.log('WebGL context released');
            }
          }
        }
      }
    } catch (e) {
      // Ignore WebGL cleanup errors
    }

    // 3. Small delay to let WebGL cleanup complete
    await new Promise(r => setTimeout(r, 50));

    // 4. Clear source (triggers internal disposal)
    viewer.src = null;
    viewer.removeAttribute('src');

    // 5. Wait for model-viewer internal disposal
    await new Promise(r => setTimeout(r, 200));

    // 6. Clear poster to free image memory
    viewer.removeAttribute('poster');

    // 7. Show loading spinner if exists
    const spinner = viewer.querySelector('.bp-model-loading, .bpz-model-loading, [slot="progress-bar"]');
    if (spinner) {
      spinner.style.display = 'flex';
    }

    // 8. Remove from tracking
    this.activeModels.delete(viewerId);

    // 9. Clean up visibility ratio for this viewer
    this.visibilityRatios.delete(viewerId);

    this.log('Disposed. Active count:', this.activeModels.size);
  }

  /**
   * Dispose the oldest (LRU) model
   */
  async disposeOldest() {
    if (this.activeModels.size === 0) return;

    // Find oldest by timestamp
    let oldestId = null;
    let oldestTime = Infinity;

    for (const [id, info] of this.activeModels) {
      if (info.timestamp < oldestTime) {
        oldestTime = info.timestamp;
        oldestId = id;
      }
    }

    if (oldestId) {
      const info = this.activeModels.get(oldestId);
      if (info && info.viewer) {
        await this.disposeModel(info.viewer);
      } else {
        // Viewer reference lost, just remove from tracking
        this.activeModels.delete(oldestId);
      }
    }
  }

  /**
   * Dispose all models (for page navigation)
   */
  async disposeAll() {
    this.log('Disposing all models. Count:', this.activeModels.size);

    const viewers = Array.from(this.activeModels.values()).map(info => info.viewer);

    for (const viewer of viewers) {
      if (viewer) {
        await this.disposeModel(viewer);
      }
    }

    this.activeModels.clear();
    this.log('All models disposed');
  }

  /**
   * Mark a model as loaded (update timestamp for LRU)
   */
  markLoaded(viewer) {
    const viewerId = this.getViewerId(viewer);
    const info = this.activeModels.get(viewerId);
    if (info) {
      info.timestamp = Date.now();
      this.log('Model loaded:', info.src?.split('/').pop());
    }
  }

  /**
   * Get current active model count
   */
  getActiveCount() {
    return this.activeModels.size;
  }

  /**
   * Check if a viewer has an active model
   */
  hasActiveModel(viewer) {
    const viewerId = this.getViewerId(viewer);
    return this.activeModels.has(viewerId);
  }

  /**
   * Initialize device-specific observers based on detected device type
   */
  initDeviceObservers() {
    this.log('Initializing device observers for:', this.deviceType);

    switch (this.deviceType) {
      case 'desktop':
        // Desktop: No observers needed - all models stay active
        this.log('Desktop mode: No observers needed');
        break;

      case 'mobile':
        // Mobile: Viewport-based activation
        this.setupViewportObserver();
        break;

      case 'tablet':
        // Tablet: Interaction-based activation
        this.setupInteractionObserver();
        // Also set up viewport observer to track visibility
        this.setupViewportObserver();
        break;
    }
  }

  /**
   * Refresh observers when new model-viewers are added to the page
   */
  refreshObservers() {
    const viewers = document.querySelectorAll('model-viewer');
    this.log('Refreshing observers for', viewers.length, 'viewers');

    viewers.forEach(viewer => {
      // Add to viewport observer if exists
      if (this.viewportObserver) {
        this.viewportObserver.observe(viewer);
      }

      // Add interaction listeners for tablet
      if (this.deviceType === 'tablet' && !this.interactionListeners.has(viewer)) {
        this.addInteractionListeners(viewer);
      }
    });
  }

  /**
   * Setup IntersectionObserver for mobile viewport-based activation
   * Tracks visibility ratio and activates the most visible model
   */
  setupViewportObserver() {
    if (this.viewportObserver) {
      this.viewportObserver.disconnect();
    }

    // Thresholds for smooth visibility tracking
    const thresholds = [0, 0.1, 0.25, 0.5, 0.75, 0.9, 1.0];

    this.viewportObserver = new IntersectionObserver((entries) => {
      let hasChanges = false;

      entries.forEach(entry => {
        const viewer = entry.target;
        const viewerId = this.getViewerId(viewer);
        const oldRatio = this.visibilityRatios.get(viewerId) || 0;
        const newRatio = entry.intersectionRatio;

        if (Math.abs(oldRatio - newRatio) > 0.05) {
          this.visibilityRatios.set(viewerId, newRatio);
          hasChanges = true;
          this.log('Visibility changed:', viewerId, oldRatio.toFixed(2), '->', newRatio.toFixed(2));
        }
      });

      // Only trigger activation check if visibility changed significantly
      // Use debounce to prevent rapid switching during fast scroll
      if (hasChanges && this.deviceType === 'mobile') {
        if (this.visibilityDebounceTimer) {
          clearTimeout(this.visibilityDebounceTimer);
        }
        this.visibilityDebounceTimer = setTimeout(() => {
          this.activateMostVisible();
        }, 150); // 150ms debounce
      }
    }, {
      threshold: thresholds,
      rootMargin: '0px'
    });

    // Observe all existing model-viewers
    document.querySelectorAll('model-viewer').forEach(viewer => {
      this.viewportObserver.observe(viewer);
    });

    this.log('Viewport observer initialized');
  }

  /**
   * Activate the model with highest visibility ratio (mobile mode)
   * Uses 25% minimum threshold to prevent rapid switching during scroll
   */
  activateMostVisible() {
    // Build viewer map ONCE (O(n) instead of O(n²))
    const viewerMap = new Map();
    document.querySelectorAll('model-viewer').forEach(v => {
      viewerMap.set(this.getViewerId(v), v);
    });

    let bestViewer = null;
    let bestRatio = 0.25; // Minimum 25% visible to activate

    // Find the most visible viewer
    for (const [viewerId, ratio] of this.visibilityRatios) {
      if (ratio > bestRatio) {
        const viewer = viewerMap.get(viewerId);
        if (viewer) {
          bestViewer = viewer;
          bestRatio = ratio;
        }
      }
    }

    if (!bestViewer) {
      this.log('No viewer meets visibility threshold');
      return;
    }

    const bestViewerId = this.getViewerId(bestViewer);

    // Check if this viewer is already active
    if (this.activeModels.has(bestViewerId) && !this.pausedModels.has(bestViewerId)) {
      return; // Already active, nothing to do
    }

    this.log('Activating most visible:', bestViewerId, 'ratio:', bestRatio.toFixed(2));

    // Pause all other models
    for (const [viewerId, info] of this.activeModels) {
      if (viewerId !== bestViewerId && info.viewer) {
        this.pauseModel(info.viewer);
      }
    }

    // Activate the best viewer
    this.activateModel(bestViewer);
  }

  /**
   * Setup interaction listeners for tablet mode
   * Activates model when user touches/clicks it
   */
  setupInteractionObserver() {
    const viewers = document.querySelectorAll('model-viewer');

    viewers.forEach((viewer, index) => {
      this.addInteractionListeners(viewer);

      // Activate first viewer by default on tablet
      if (index === 0) {
        this.log('Tablet: Activating first model by default');
        this.activateModel(viewer);
      }
    });

    this.log('Interaction observer initialized for', viewers.length, 'viewers');
  }

  /**
   * Add touch/click listeners to a viewer for tablet interaction mode
   */
  addInteractionListeners(viewer) {
    if (this.interactionListeners.has(viewer)) {
      return; // Already has listeners
    }

    const handleInteraction = (e) => {
      const viewerId = this.getViewerId(viewer);

      // Skip if already the active model
      if (this.activeModels.has(viewerId) && !this.pausedModels.has(viewerId)) {
        return;
      }

      this.log('Tablet interaction on:', viewerId);

      // Pause all other models
      for (const [id, info] of this.activeModels) {
        if (id !== viewerId && info.viewer) {
          this.pauseModel(info.viewer);
        }
      }

      // Activate this model
      this.activateModel(viewer);
    };

    viewer.addEventListener('pointerdown', handleInteraction, { passive: true });
    viewer.addEventListener('touchstart', handleInteraction, { passive: true });

    this.interactionListeners.set(viewer, {
      pointerdown: handleInteraction,
      touchstart: handleInteraction
    });
  }

  /**
   * Pause a model (lighter than dispose - keeps WebGL context)
   * Stops rendering but allows fast reactivation (~100ms vs ~500ms reload)
   * @param {Element} viewer - The model-viewer element
   */
  pauseModel(viewer) {
    const viewerId = this.getViewerId(viewer);

    // Skip if already paused or not active
    if (this.pausedModels.has(viewerId)) {
      return;
    }

    const info = this.activeModels.get(viewerId);
    if (!info) {
      return;
    }

    this.log('Pausing model:', viewerId);

    // Store state for reactivation
    const wasAutoRotating = viewer.autoRotate;

    // Stop auto-rotate and disable camera controls (reduces GPU rendering)
    viewer.autoRotate = false;
    viewer.cameraControls = false;

    // Pause animation if playing
    if (viewer.pause) {
      viewer.pause();
    }

    // Move to paused state
    this.pausedModels.set(viewerId, {
      src: info.src,
      viewer: viewer,
      wasAutoRotating: wasAutoRotating
    });

    // Remove from active (but keep in pausedModels for quick reactivation)
    this.activeModels.delete(viewerId);

    this.log('Model paused. Active:', this.activeModels.size, 'Paused:', this.pausedModels.size);
  }

  /**
   * Activate a model (quick reactivation if paused, or full load if new)
   * @param {Element} viewer - The model-viewer element
   */
  activateModel(viewer) {
    const viewerId = this.getViewerId(viewer);

    // Check if paused (quick reactivation path)
    if (this.pausedModels.has(viewerId)) {
      const pausedInfo = this.pausedModels.get(viewerId);

      this.log('Quick reactivating paused model:', viewerId);

      // Re-enable camera controls
      viewer.cameraControls = true;

      // Restore auto-rotate if it was on
      if (pausedInfo.wasAutoRotating) {
        viewer.autoRotate = true;
      }

      // Move back to active
      this.activeModels.set(viewerId, {
        src: pausedInfo.src,
        timestamp: Date.now(),
        viewer: viewer
      });

      this.pausedModels.delete(viewerId);

      this.log('Model reactivated. Active:', this.activeModels.size, 'Paused:', this.pausedModels.size);
      return;
    }

    // Not paused - check if it has a src to load
    const src = viewer.getAttribute('src') || viewer.dataset.src;
    if (src) {
      // Register as active
      this.activeModels.set(viewerId, {
        src: src,
        timestamp: Date.now(),
        viewer: viewer
      });

      // Ensure controls are enabled
      viewer.cameraControls = true;

      this.log('Model activated:', viewerId, 'Active:', this.activeModels.size);
    }
  }

  /**
   * Cleanup observers on destroy
   */
  destroy() {
    // Clear debounce timer
    if (this.visibilityDebounceTimer) {
      clearTimeout(this.visibilityDebounceTimer);
      this.visibilityDebounceTimer = null;
    }

    if (this.viewportObserver) {
      this.viewportObserver.disconnect();
      this.viewportObserver = null;
    }

    // Remove interaction listeners
    document.querySelectorAll('model-viewer').forEach(viewer => {
      const listeners = this.interactionListeners.get(viewer);
      if (listeners) {
        viewer.removeEventListener('pointerdown', listeners.pointerdown);
        viewer.removeEventListener('touchstart', listeners.touchstart);
      }
    });

    this.visibilityRatios.clear();
    this.pausedModels.clear();
    this.modelsBeforeHide = [];

    this.log('Observers destroyed');
  }
}

// Global singleton instance
window.modelLimitManager = window.modelLimitManager || new ModelLimitManager();

// Page navigation cleanup
(function() {
  function cleanupAllModels() {
    if (window.modelLimitManager) {
      window.modelLimitManager.disposeAll();
    }

    // Also clear any viewer instances
    if (window.bpViewerInstances) {
      window.bpViewerInstances.forEach(inst => {
        if (inst && inst.destroy) {
          inst.destroy();
        }
      });
      window.bpViewerInstances = [];
    }

    // Fallback: clear all model-viewer src attributes
    document.querySelectorAll('model-viewer').forEach(viewer => {
      viewer.src = null;
      viewer.removeAttribute('src');
      viewer.removeAttribute('poster');
    });
  }

  // Standard page unload
  window.addEventListener('beforeunload', cleanupAllModels);

  // Shopify Ajax navigation (if using)
  document.addEventListener('page:unload', cleanupAllModels);

  // Back/forward cache prevention
  window.addEventListener('pagehide', (event) => {
    if (event.persisted) {
      cleanupAllModels();
    }
  });

  // Visibility change - dispose when tab hidden, reload when visible
  document.addEventListener('visibilitychange', () => {
    if (!window.modelLimitManager) return;

    if (document.hidden) {
      // Tab hidden: save current model state before disposing
      console.log('[ModelLimitManager] Tab hidden, disposing models to save memory');

      // Store active model info for reload
      window.modelLimitManager.modelsBeforeHide = [];
      for (const [viewerId, info] of window.modelLimitManager.activeModels) {
        if (info.viewer && info.src) {
          window.modelLimitManager.modelsBeforeHide.push({
            viewer: info.viewer,
            src: info.src
          });
        }
      }
      window.modelLimitManager.disposedOnHide = true;
      window.modelLimitManager.disposeAll();
    } else {
      // Tab visible: reload models if they were disposed
      if (window.modelLimitManager.disposedOnHide && window.modelLimitManager.modelsBeforeHide.length > 0) {
        console.log('[ModelLimitManager] Tab visible, reloading models');

        // Only reload on mobile/tablet (desktop doesn't need optimization)
        if (window.modelLimitManager.deviceType !== 'desktop') {
          // Reload only the first model (most visible will take over via observer)
          const firstModel = window.modelLimitManager.modelsBeforeHide[0];
          if (firstModel && firstModel.viewer && firstModel.src) {
            firstModel.viewer.src = firstModel.src;
            window.modelLimitManager.activeModels.set(
              window.modelLimitManager.getViewerId(firstModel.viewer),
              {
                src: firstModel.src,
                timestamp: Date.now(),
                viewer: firstModel.viewer
              }
            );
          }
        } else {
          // Desktop: reload all models
          window.modelLimitManager.modelsBeforeHide.forEach(model => {
            if (model.viewer && model.src) {
              model.viewer.src = model.src;
              window.modelLimitManager.activeModels.set(
                window.modelLimitManager.getViewerId(model.viewer),
                {
                  src: model.src,
                  timestamp: Date.now(),
                  viewer: model.viewer
                }
              );
            }
          });
        }

        window.modelLimitManager.modelsBeforeHide = [];
        window.modelLimitManager.disposedOnHide = false;
      }
    }
  });
})();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ModelLimitManager;
}
